import Backbone from 'backbone';
import 'backbone.paginator';
import _ from 'lodash';
import {getBSEntryRequestParameters} from 'module/BSEntry/BSRestAPI';
import {checkPersurlRemoteCollision} from 'module/BSEntry/checkPersurlRemoteCollision';
import {EntityAttributes, ET_BOOKING, ET_BOOKING_URL, ET_PERSURL, ET_PERSURL_URL} from 'module/BSEntry/EntryTypeParams';
import moment from 'moment';
import {convertPromiseToDeferred} from 'util/PromiseUtil';
import {getSisDateAsNumber, getSisDateAsString, getSisTimeAsISO, getSisTimeAsNumber, MomFmt, parseSisDate, parseSisDateTime, parseSisDuration} from 'util/SisDateTime';
import {objectifyStringPath} from 'util/StringUtil';
import {ZspArt} from 'module/edesc/ZspArt';

/**
 * For methods, that are identical on both, PersurlModel and BookingModel
 * requires following Properties:
 * * serverAttributes
 * * editableAttributes
 * generates
 * * persistedAttributes
 */
const BaseModel = Backbone.Model.extend({
	initialize: function () {
		this.savePersistedAttributes();
		this.listenTo(this, 'sync', this.savePersistedAttributes);
	},
	toJSON: function () {
		return _.pick(objectifyStringPath(this.attributes), this.serverAttributes);
	},
	//todo use this - needs refactoring of all attributes read usages, and more graceful this.serverAttributes / this.editableAttributes
	//parse: function (attr) {
	//	return stringifyObjectPath(attr);
	//},
	savePersistedAttributes: function () {
		this.persistedAttributes = this.pick(this.serverAttributes);
	},
	hasChangesToPersist: function () {
		return !_.isEqual(
			_.pick(this.attributes, this.editableAttributes),
			_.pick(this.persistedAttributes, this.editableAttributes),
		);
	},
	/**
	 * overrides set to always convert numeric values if defined in this.floatAttributes or this.intAttributes
	 * @param key
	 * @param val
	 * @param options
	 * @returns {*}
	 */
	set: function (key, val, options) {
		var that = this;
		// BEGIN dynamic method parameters from Backbone
		if (key == null) return this;
		var attrs;
		// Handle both `"key", value` and `{key: value}` -style arguments.
		if (typeof key === 'object') {
			attrs = key;
			options = val;
		} else {
			(attrs = {})[key] = val;
		}
		// END dynamic method parameters from Backbone
		_.each(attrs, function (attrVal, attrName) {
			if (_.includes(that.intAttributes, attrName)) {
				attrs[attrName] = parseInt(attrVal);
			} else if (_.includes(that.floatAttributes, attrName)) {
				attrs[attrName] = parseFloat(attrVal);
			}
		});
		return Backbone.Model.prototype.set.call(this, attrs, options);
	},
});

/**
 * @typedef BookingModel BSModuleBB.BookingModel
 */
export const BookingModel = BaseModel.extend({
	urlRoot: ET_BOOKING_URL, // set in application in BSModuleBB.BookingModel.prototype.urlRoot
	idAttribute: 'bookingNr',
	defaults: {
		sisName: ET_BOOKING,
		persNr: 0,
		bookingDate: 0,
		bookingTime: 0,
		bookingTimespan: 0,
		bookingType: ' ',
		hourCategory: ' ',
		bewEinh: ' ',
		empfEinh: ' ',
		einstuf: 0,
		text: ' ',
	},
	//todo remove/open up for custom fields
	serverAttributes: EntityAttributes[ET_BOOKING],
	//todo remove/open up for custom fields
	editableAttributes: ['persNr', 'bookingDate', 'bookingTime', 'bookingTimespan', 'bookingType', 'hourCategory', 'bewEinh', 'empfEinh', 'einstuf', 'text', 'mdat', 'cf'],
	intAttributes: ['persNr', 'bookingDate', 'bookingTime', 'bookingTimespan'],
	floatAttributes: ['einstuf'],
	getSisName: function () {
		return this.get('sisName');
	},
	hasChangesToPersist: function () {
		// buchzeit ignore seconds
		const attributes = {...this.attributes};
		const persistedAttributes = {...this.persistedAttributes};
		attributes.bookingTime = getSisTimeAsNumber(attributes.bookingTime, {ignoreSeconds: true});
		persistedAttributes.bookingTime = getSisTimeAsNumber(persistedAttributes.bookingTime, {ignoreSeconds: true});
		return !_.isEqual(
			_.pick(attributes, this.editableAttributes),
			_.pick(persistedAttributes, this.editableAttributes),
		);
	},
	parse: function (json) {
		return BaseModel.prototype.parse(_.omit(json, 'edit')); //todo remove server attribute, no longer needed on client
	},
	// blacklist for server communication
	getStartDateTime: function () {
		return parseSisDateTime(this.get('bookingDate'), this.get('bookingTime'), {ignoreSeconds: true});
	},
	getEndDateTime: function () {
		let ret = this.getStartDateTime();
		if (this.meta && this.meta.zspArt === ZspArt.Bereich ||
			// bug: meta not initialized for not rendered entries.
			// these entries are important for overlap check -> for ZS default to ZspArt.Bereich
			!this.meta && this.get('bookingType') === 'ZS') {
			// default to ZspArt.Bereich because meta is only set for rendered entries.
			ret = ret.add(this.getDuration());
		}
		return ret;
	},
	getDuration: function () {
		return parseSisDuration(this.get('bookingTimespan'));
	},
	/**
	 * todo refactor to use methods with datetime and remove
	 * @returns {moment.moment} HH:mm
	 */
	getEndTime: function () {
		return this.getEndDateTime();
	},
	/**
	 * todo refactor to use methods with datetime and remove
	 * compatibility function
	 * @param date
	 */
	setStartDate: function (date) {
		date = getSisDateAsNumber(date);
		this.set('bookingDate', date)
	},
	toStringFormat: function () { //todo cfdef
		var ret = '';
		var attr = this.attributes;
		ret += '(' + attr.persNr + '), ';
		ret += attr.bookingType + ' / ' + attr.hourCategory + ', ';
		ret += getSisDateAsString(attr.bookingDate) + ' ' + getSisTimeAsISO(attr.bookingTime) + ', [';
		ret += attr.bewEinh + ',';
		ret += attr.empfEinh + '],';
		ret += attr.einstuf + ',';
		ret += attr.text;
		return ret;
	},
});

export const AzeBookingModel = BookingModel.extend({
	idAttribute: 'bookingNr',
	defaults: {
		sisName: 'azebooking',
		bookingNr: null,
		persNr: 0,
		bookingDate: 0,
		bookingTime: 0,
		bookingTimeTo: 0,
		bookingTimespan: 0,
		text: ' ',
		koWertList: [],
	},
	serverAttributes: ['bookingNr', 'persNr', 'bookingDate', 'bookingTime', 'bookingTimespan', 'bookingType', 'hourCategory', 'bewEinh', 'empfEinh', 'einstuf', 'text', 'koWertList'],
	editableAttributes: ['persNr', 'bookingDate', 'bookingTime', 'bookingTimespan', 'bookingType', 'hourCategory', 'bewEinh', 'empfEinh', 'einstuf', 'text', 'koWertList'],
	urlRoot: function () {
		return '../json/azebooking';
	},
	getSisName: function () {
		return this.get('sisName');
	},
});

/**
 * @typedef PersurlModel BSModuleBB.PersurlModel
 */
export const PersurlModel = BaseModel.extend({
	urlRoot: ET_PERSURL_URL, // set in application in BSModuleBB.PersurlModel.prototype.urlRoot
	defaults: {
		sisName: ET_PERSURL,
		'persNr': 0,
		'start': 0,
		'end': 0,
		'hourCategory': ' ',
		'openMarking': ' ',
		'representative': 0,
		'altRepresentative': 0,
		'text': '',
	},
	intAttributes: ['id', 'persNr', 'start', 'end', 'representative', 'altRepresentative'],
	serverAttributes: EntityAttributes[ET_PERSURL],
	editableAttributes: ['persNr', 'start', 'end', 'hourCategory', 'openMarking', 'representative', 'altRepresentative', 'text', 'cf'],
	getSisName: function () {
		return this.get('sisName');
	},
	url: function () {
		return getBSEntryRequestParameters(this.attributes, this.isNew() ? undefined : this.persistedAttributes).url;
	},
	getStartDateTime: function () {
		return parseSisDate(this.get('start'));
	},
	getEndDateTime: function () {
		return parseSisDate(this.get('end')).endOf('day');
	},
	getDuration: function () {
		return moment.duration(parseSisDate(this.get('end')).add(1, 'd').diff(this.getStartDateTime()));
	},
	/**
	 * todo refactor to use methods above and remove
	 * compatibility function
	 * @param date
	 */
	setStartDate: function (date) {
		date = getSisDateAsNumber(date);
		this.set('start', date)
	},
	toStringFormat: function () {
		var ret = '';
		var attr = this.attributes;
		ret += '(' + attr.persNr + '), ';
		ret += attr.hourCategory + ', ';
		ret += getSisDateAsString(attr.start) + ' - ' + getSisDateAsString(attr.end);
		if (attr.text && attr.text.trim()) ret += ', ' + attr.text;
		return ret;
	},
});

////////////////////////////////////////////////////////////////////////////////////////
//////////// Collections ///////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////

/**
 * functions that are in common of the collections can be called directly on the instance and will be forwarded.
 * an array of the return values will be returned.
 *
 * _all: collections as array
 * _collections: object
 *
 * collections require following function implementation atm:
 *      getFor(row,column)
 *
 * @param {object} [options.collections] = {collectionName: collectionInstance} all object members get imported
 *
 */
export function EntryCollection(options) {
	//(private)
	var that = this;

	//(public)
	// keep those 2 in sync!
	this._all;
	this._collections;

	//(private)

	var _each = function (func) {
		var args = _.drop(arguments); // slice
		var ret = [];
		_.each(this._all, function (col) {
			ret.push(col[func].apply(col, args));
		});

		ret = _.flatten(ret);
		return ret;
	};
	//(public)
	/**
	 *
	 * @param {object} collections A Collection in the format {collectionName: collectionInstance}
	 */
	this.addCollections = function (collections) {
		// set public anchor to collections
		if (!_.isPlainObject(collections)) throw 'Not an object of collections!';
		this._collections = this._collections || {};
		this._all = this._all || [];
		_.assign(this._collections, collections);
		this._all = _.values(this._collections);
		// reflection of collection-functions to entryCollection.
		let allFuncNames = _.intersection.apply(null, _.map(that._all, function (f) {
			return _.functionsIn(f)
		}));
		allFuncNames = _.omit(allFuncNames, ['listenTo', 'trigger']);
		//todo :
		// allFuncNames = _.filter(allFuncNames, ['filter', 'findCollision', 'add','getFor','remove','fetch']);

		let extension = {};
		_.each(allFuncNames, function (fname) {
			extension[fname] = _.partial(_each, fname);
		});
		_.defaults(this, extension);
	};

	/**
	 * Takes single model or array of models and checks instance of model for the corresponding collection before adding.
	 * @requires PersurlCollection & BookingCollection instances
	 * @param models {PersurlModel | BookingModel | Array}
	 */

	this.add = function (models) {
		if (!models) return;
		var list = [];
		// single model
		if (!_.isArray(models)) {
			list.push(models);
		} else {
			//array of models
			list = models;
		}
		var listPersurl = [];
		var listBooking = [];
		_.each(list, function (m) {
			if (m.getSisName() === ET_PERSURL) {
				listPersurl.push(m);
			} else if (m.getSisName() === ET_BOOKING) {
				listBooking.push(m);
			}
		});
		if (this._collections.persurl) this._collections.persurl.add(listPersurl);
		if (this._collections.booking) this._collections.booking.add(listBooking);
	};


	/**
	 *
	 * @param attributes
	 * @param options
	 * @param options.checkLocalCollision checks locally for collisions
	 * @param options.checkRemoteCollision implies local check for robustness, also checks on server
	 * @return {{}}
	 */

		//constructor
	let _constructor = function (that, options) {
			that.eventBus = new Backbone.View();
			if (options) {
				if (options.collections) that.addCollections(options.collections);
			}
			that.trigger = that.eventBus.trigger;
			that.listenTo = that.eventBus.listenTo;
		}(this, options);
}

/**
 * @requires extend properties for app: url and model
 */
export const PersurlCollection = Backbone.Collection.extend({
	model: PersurlModel,
	url: '../json/persurls', // to setup use BSModuleBB.PersurlCollection.prototype.url
	/**
	 * @param id {int} persNr     [row]
	 * @param date {int} SisDate  [column]
	 */
	getFor: function (id, date) {
		date = parseInt(date);
		return this.filter(function (m) {
			var persNr = parseInt(m.get('persNr'));
			var start = parseInt(m.get('start'));
			var end = parseInt(m.get('end'));
			//return valid for this day and new on this day
			return (persNr == id) && ((start <= date && end >= date) || (m.isNew() && start == date));
		});
	},
	/**
	 * returns true if collision found
	 * implemented in booking and persurl
	 *
	 * @param attributes
	 * @param attributes.start of new model
	 * @param attributes.end of new model
	 * @param attributes.persNr of new model
	 *
	 * @param options
	 * @param options.excludeModel check collision whithout this model
	 * @param options.singleDay used for initialisation - end date is overridden with start date
	 * @param options.checkLocalCollision check local collection for collisions (other dates than on client).
	 * @param options.checkLocalCollisionIncludeNew include new models in local collision check
	 * @param options.checkRemoteCollision check server status for collisions (other dates than on client).
	 *
	 * @returns {[] | $.Deferred} false, conflicting models, Deferred only if options.checkRemoteCollision is true
	 */
	findCollision: function (attributes, options) {
		options = options || {};

		if (!_.has(attributes, 'start')) return []; //defines persurl
		var persNr = parseInt(attributes.persNr);

		var excludedModel = options.excludeModel;
		var checkLocalCollisionIncludeNew = options.checkLocalCollisionIncludeNew;
		var singleDay = options.singleDay;
		var checkLocalCollision = options.checkLocalCollision;
		var checkRemoteCollision = options.checkRemoteCollision;

		var start = attributes.start;
		var end = attributes.end;
		if (singleDay) end = start;
		start = parseSisDate(start);
		end = parseSisDate(end);
		if (!start.isValid() || !end.isValid()) return [];
		start = getSisDateAsNumber(start);
		end = getSisDateAsNumber(end);

		var localCollisions;
		if (checkLocalCollision) {
			if (checkRemoteCollision) {
				return convertPromiseToDeferred(
					checkPersurlRemoteCollision(excludedModel && excludedModel.persistedAttributes, attributes, options),
				);
			}
			var models = _.reject(this.models, function (m) {
				if (m.get('persNr') !== persNr) return true;
				if (excludedModel && (m.id === excludedModel.id || m.cid === excludedModel.cid)) return true;
				if (!checkLocalCollisionIncludeNew && m.isNew()) return true;
			});

			//// from models/PersurlModel
			localCollisions = _.filter(models, function (model) {
				var otherStart = getSisDateAsNumber(model.get('start'));
				var otherEnd = getSisDateAsNumber(model.get('end'));

				var selfStartOk = start < otherStart || start > otherEnd;
				var selfEndOk = end < otherStart || end > otherEnd;
				var otherStartOk = otherStart < start || otherStart > end;
				var otherEndOk = otherEnd < start || otherEnd > end;

				return !(selfStartOk && selfEndOk && otherStartOk && otherEndOk);
			});
		}
		var collisionCollection = new Backbone.Collection(localCollisions);
		return collisionCollection.models;
	},
	// end Collection
});

/**
 * @requires extend properties for app: url and model
 */
export const BookingCollection = Backbone.Collection.extend({
	model: BookingModel,
	url: '../json/bookings', // to setup use BSModuleBB.BookingCollection.prototype.url
	/**
	 * @param {int} id persNr     [row]
	 * @param {int} date SisDate  [column]
	 * @param {[string]} [bewEinhArray] visible bewEinh id string  [column]
	 * @param {{}} [options]
	 * @param {boolean} [options.ignoreBewEinhForNew] include always models that are not persisted
	 */
	getFor: function (id, date, bewEinhArray, options) {
		var ignoreBewEinhForNew = options && options.ignoreBewEinhForNew;
		id = parseInt(id);
		date = parseInt(parseSisDate(date).format(MomFmt.DATE));
		var models = this.where({persNr: id, bookingDate: date});
		if (bewEinhArray) models = _.filter(models, function (m) {
			return ignoreBewEinhForNew && m.isNew() || _.includes(bewEinhArray, m.get('bewEinh'));
		});
		return models;
	},
	/**
	 * returns true if collision found
	 * implemented in booking and persurl
	 *
	 * @param attributes {Object}
	 * @param attributes.bookingDate of new model
	 * @param attributes.bookingType of new model
	 * @param attributes.hourCategory of new model
	 * @param attributes.persNr of new model
	 * @param attributes.bewEinh of new model
	 *
	 * @param options {Object}
	 * @param options.excludeModel {Backbone.Model} check collision whithout this model
	 * @param options.checkLocalCollision {boolean} must be true
	 * @param options.checkRemoteCollision {boolean} *not implemented*
	 * @param options.checkLocalCollisionIncludeNew {boolean} include new models in local collision check
	 * @param [options.ignoreBewEinh] {boolean} use when working with multiple costCenters at once
	 *
	 * @returns {[]} conflicting models
	 */
	findCollision: function (attributes, options) {
		options = options || {};
		if (!options.checkLocalCollision) return [];

		var bookingType = attributes.bookingType;
		var persNr = parseInt(attributes.persNr);
		var bookingDate = getSisDateAsNumber(attributes.bookingDate);
		var hourCategory = attributes.hourCategory;
		var bewEinh = attributes.bewEinh;

		//options
		var excludedModel = options.excludeModel;
		var checkLocalCollisionIncludeNew = options.checkLocalCollisionIncludeNew;

		// #getSisDateAsNumber to ignore time fields
		var searchObj = {
			persNr: persNr,
			bookingDate: bookingDate,
			bookingType: bookingType,
			hourCategory: hourCategory,
			bewEinh: bewEinh,
		};
		if (options.ignoreBewEinh) searchObj = _.omit(searchObj, 'bewEinh');
		var searchObjKeys = _.keysIn(searchObj);
		return _.reject(this.models, function (m) {
			if (!_.isEqual(m.pick(searchObjKeys), searchObj)) return true;
			if (excludedModel && (m.id === excludedModel.id || m.cid === excludedModel.cid)) return true;
			if (!checkLocalCollisionIncludeNew && m.isNew()) return true;
		});
	},
});

export default {
	PersurlModel: PersurlModel,
	BookingModel: BookingModel,
	AzeBookingModel: AzeBookingModel,

	PersurlCollection: PersurlCollection,
	BookingCollection: BookingCollection,
	EntryCollection: EntryCollection,
};